// Marketing site vs. application host split.
//
// In production the public marketing page lives on the root domain
// (clozdl.com) and the actual CRM app lives on app.clozdl.com. This module is
// the single source of truth for "which host am I on" and "where do I send the
// user for the app / for marketing".
//
// Hosts come from Vite build args so non-prod (localhost, previews) Just Works:
//   VITE_MARKETING_HOST  e.g. "clozdl.com"
//   VITE_APP_HOST        e.g. "app.clozdl.com"
// When either is unset (local dev), the split is disabled and the app behaves
// as a single-host SPA exactly as before.

const MARKETING_HOST = import.meta.env.VITE_MARKETING_HOST || "";
const APP_HOST = import.meta.env.VITE_APP_HOST || "";

// The split only applies when both hosts are configured AND differ. In dev they
// are blank, so `splitEnabled` is false and every helper degrades to same-host
// behavior.
export const splitEnabled = Boolean(
  MARKETING_HOST && APP_HOST && MARKETING_HOST !== APP_HOST,
);

function currentHost() {
  return typeof window !== "undefined" ? window.location.host : "";
}

// True when the browser is on the public marketing host (clozdl.com).
export function onMarketingHost() {
  return splitEnabled && currentHost() === MARKETING_HOST;
}

// True when on the app host (app.clozdl.com) — or always, when the split is off
// (single-host dev), so app routing stays active locally.
export function onAppHost() {
  return !splitEnabled || currentHost() === APP_HOST;
}

// True ONLY when the split is enabled AND we're on the app host. Unlike
// onAppHost(), this is false in single-host dev — use it to gate behavior that
// should only happen on the real app.clozdl.com (e.g. hiding the marketing page
// at "/"), so dev keeps showing the landing page.
export function isDedicatedAppHost() {
  return splitEnabled && currentHost() === APP_HOST;
}

// Absolute URL to a path on the app host, e.g. appUrl("/dashboard").
// When the split is off, returns the plain path (same-host navigation).
export function appUrl(path = "/") {
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!splitEnabled) return p;
  const protocol =
    typeof window !== "undefined" ? window.location.protocol : "https:";
  return `${protocol}//${APP_HOST}${p}`;
}

// Absolute URL to the marketing host root.
export function marketingUrl(path = "/") {
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!splitEnabled) return p;
  const protocol =
    typeof window !== "undefined" ? window.location.protocol : "https:";
  return `${protocol}//${MARKETING_HOST}${p}`;
}

// Send the browser to a path on the app host (full navigation, crosses origin).
export function goToApp(path = "/dashboard") {
  if (typeof window !== "undefined") window.location.assign(appUrl(path));
}
